
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { getSessionUserId, getSessionUserName } from '../../../../../utils/SessionUser';

const EMPTY = {
  idPersona: 0,
  nota: '',
  registradoPor: '',
  tipo: '',
  fechaRegistro: new Date().toISOString(),
};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  fkValue = 0,
  onClose,
  onSubmit,
  inline = false,
}) {
  EMPTY.idPersona = fkValue;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    tipo: '',
    nota: '',
    registradoPor: '',
  });

  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'tipo':
        if (!value) error = 'Selecciona un tipo.';
        break;
      case 'nota':
        if (!value) error = 'Captura la nota.';
        break;
      case 'registradoPor':
        if (!value) error = 'Captura quién registró la observación.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    return !error;
  };

  const validateAll = () => {
    const okTipo = validateField('tipo', form.tipo);
    const okNota = validateField('nota', form.nota);
    const okReg = validateField('registradoPor', form.registradoPor);
    return okTipo && okNota && okReg;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    validateField(field, value);
  };

  useEffect(() => {
    if (mode === 'edit' && data) {
      const filled = {
        idPersona: data.idPersona ?? fkValue ?? 0,
        nota: data.nota ?? '',
        registradoPor: data.registradoPor ?? '',
        tipo: data.tipo ?? '',
        fechaRegistro: data.fechaRegistro ?? new Date().toISOString(),
      };
      setForm(filled);
      validateField('tipo', filled.tipo);
      validateField('nota', filled.nota);
      validateField('registradoPor', filled.registradoPor);
    } else {
      setForm({
        ...EMPTY,
        idPersona: fkValue,
      });
      setErrors({ tipo: '', nota: '', registradoPor: '' });
    }
  }, [mode, data, fkValue]);

  useEffect(() => {
    if (mode === 'new') {
      const fetchCurrentUserName = async () => {
        const sessionName = getSessionUserName() || '';
        setForm((prev) => ({ ...prev, registradoPor: sessionName }));
        if (sessionName) setErrors((prev) => ({ ...prev, registradoPor: '' }));

        try {
          const idUser = getSessionUserId();
          if (!idUser) return;

          const resUser = await axios.get(ENDPOINTS.usuarioById(idUser));
          const idPersona = resUser.data?.idPersona;
          if (!idPersona) return;

          const resPersona = await axios.get(ENDPOINTS.personaById(idPersona));
          const p = resPersona.data;
          if (p) {
            const fullName = `${p.nombre || ''} ${p.apellidoPaterno || ''} ${p.apellidoMaterno || ''}`.trim();
            if (fullName) {
              setForm((prev) => ({ ...prev, registradoPor: fullName }));
              setErrors((prev) => ({ ...prev, registradoPor: '' }));
            }
          }
        } catch (e) {
          console.error(e);
        }
      };
      fetchCurrentUserName();
    }
  }, [mode]);

  const isSaveDisabled =
    !form.tipo?.trim() ||
    !form.nota?.trim() ||
    !form.registradoPor?.trim() ||
    !!errors.tipo ||
    !!errors.nota ||
    !!errors.registradoPor;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '.25rem',
  };

  if (!open) return null;

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nueva observación' : 'Editar observación'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '15%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Tipo:</label>
        <br /><br />
        <select
          className={`infoField${errors.tipo ? ' infoField--error' : ''}`}
          value={form.tipo}
          onChange={(e) => handleChange('tipo', e.target.value)}
        >
          <option value="">Seleccione…</option>
          <option value="General">General</option>
          <option value="Alerta">Alerta</option>
          <option value="Seguimiento">Seguimiento</option>
          <option value="Médica">Médica</option>
        </select>
        {errors.tipo && <small style={errorStyle}>{errors.tipo}</small>}
      </div>

      <div style={{ gridColumn: '1 / -1' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Nota:</label>
        <br /><br />
        <textarea
          className={`infoField${errors.nota ? ' infoField--error' : ''}`}
          value={form.nota}
          onChange={(e) => handleChange('nota', e.target.value)}
          maxLength={500}
          rows={4}
        />
        {errors.nota && <small style={errorStyle}>{errors.nota}</small>}
      </div>

    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => {
          if (!validateAll()) return;
          onSubmit(form, mode);
        }}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(720px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
